import { useState, useEffect, useMemo } from 'react';
import { getCampaignRecipients } from '../../api/admin';
import type { CampaignRecipientsResponse } from '../../api/admin';

interface RecipientsModalProps {
  campaignId: string;
  campaignName: string;
  onClose: () => void;
}

export default function RecipientsModal({ campaignId, campaignName, onClose }: RecipientsModalProps) {
  const [data, setData] = useState<CampaignRecipientsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [suppressedOpen, setSuppressedOpen] = useState(false);

  const fetchRecipients = async () => {
    try {
      setLoading(true);
      setError(null);
      const result = await getCampaignRecipients(campaignId);
      setData(result);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load recipients');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRecipients();
  }, [campaignId]);

  const filtered = useMemo(() => {
    if (!data) return [];
    if (!search.trim()) return data.recipients;
    const q = search.toLowerCase();
    return data.recipients.filter(
      (r) =>
        r.studio_name.toLowerCase().includes(q) ||
        r.contact_name.toLowerCase().includes(q) ||
        r.email.toLowerCase().includes(q)
    );
  }, [data, search]);

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div
        className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[80vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-warm-100">
          <div className="min-w-0">
            <h3 className="text-lg font-semibold text-warm-900 truncate">{campaignName}</h3>
            {data && (
              <p className="text-sm text-warm-500 mt-0.5">
                {data.total} {data.total === 1 ? 'recipient' : 'recipients'}
              </p>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1 text-warm-400 hover:text-warm-600 rounded flex-shrink-0"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Search */}
        {!loading && !error && data && data.recipients.length > 0 && (
          <div className="px-5 py-3 border-b border-warm-100">
            <input
              type="text"
              placeholder="Search by studio, contact, or email..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full px-3 py-2.5 border border-warm-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none text-sm text-warm-900 placeholder:text-warm-400"
            />
          </div>
        )}

        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="p-5 space-y-3">
              {[1, 2, 3, 4, 5].map((i) => (
                <div key={i} className="h-12 bg-warm-100 rounded-lg animate-pulse" />
              ))}
            </div>
          ) : error ? (
            <div className="text-center py-12 px-5">
              <p className="text-warm-600 font-medium">Failed to load recipients</p>
              <p className="text-sm text-warm-400 mt-1">{error}</p>
              <button
                type="button"
                onClick={fetchRecipients}
                className="mt-4 px-4 py-2 text-sm font-medium text-primary-600 border border-primary-300 rounded-lg hover:bg-primary-50 transition-colors"
              >
                Retry
              </button>
            </div>
          ) : data && data.recipients.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-warm-600 font-medium">No recipients</p>
              <p className="text-sm text-warm-400 mt-1">No studios match the campaign targeting criteria.</p>
            </div>
          ) : (
            <>
              {/* Desktop table */}
              <div className="hidden sm:block">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-warm-100">
                      <th className="text-left text-sm font-semibold text-warm-700 px-5 py-3">Studio Name</th>
                      <th className="text-left text-sm font-semibold text-warm-700 px-5 py-3">Contact</th>
                      <th className="text-left text-sm font-semibold text-warm-700 px-5 py-3">Email</th>
                      <th className="text-left text-sm font-semibold text-warm-700 px-5 py-3">City</th>
                      <th className="text-left text-sm font-semibold text-warm-700 px-5 py-3">State</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-warm-50">
                    {filtered.map((r, i) => (
                      <tr key={i} className="hover:bg-warm-50 transition-colors">
                        <td className="text-sm text-warm-900 font-medium px-5 py-3">{r.studio_name}</td>
                        <td className="text-sm text-warm-600 px-5 py-3">{r.contact_name}</td>
                        <td className="text-sm text-warm-600 px-5 py-3">{r.email}</td>
                        <td className="text-sm text-warm-600 px-5 py-3">{r.city}</td>
                        <td className="text-sm text-warm-600 px-5 py-3">{r.state}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {search && filtered.length === 0 && (
                  <p className="text-sm text-warm-400 text-center py-8">No recipients match "{search}"</p>
                )}
              </div>

              {/* Mobile list */}
              <div className="sm:hidden divide-y divide-warm-50">
                {filtered.map((r, i) => (
                  <div key={i} className="px-5 py-3">
                    <p className="text-sm font-medium text-warm-900">{r.studio_name}</p>
                    <p className="text-xs text-warm-600 mt-0.5">{r.contact_name}</p>
                    <p className="text-xs text-warm-500">{r.email}</p>
                    <p className="text-xs text-warm-400 mt-0.5">{r.city}{r.state ? `, ${r.state}` : ''}</p>
                  </div>
                ))}
                {search && filtered.length === 0 && (
                  <p className="text-sm text-warm-400 text-center py-8">No recipients match "{search}"</p>
                )}
              </div>

              {/* Suppressed section */}
              {data && data.suppressed.booked_count > 0 && (
                <div className="bg-warm-50 border-t border-warm-200">
                  <button
                    type="button"
                    onClick={() => setSuppressedOpen(!suppressedOpen)}
                    className="w-full flex items-center justify-between px-5 py-3 text-left group"
                    title="These studios have confirmed or pending bookings for this show and won't receive this email"
                  >
                    <span className="text-sm font-medium text-warm-600">
                      Suppressed ({data.suppressed.booked_count} {data.suppressed.booked_count === 1 ? 'studio' : 'studios'} booked this show)
                    </span>
                    <svg
                      className={`w-4 h-4 text-warm-400 transition-transform ${suppressedOpen ? 'rotate-180' : ''}`}
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                  {suppressedOpen && (
                    <div className="px-5 pb-4 space-y-1.5">
                      {data.suppressed.booked_studios.map((name, i) => (
                        <p key={i} className="text-sm text-warm-500">{name}</p>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="px-5 py-4 border-t border-warm-100 flex justify-end">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 border border-warm-300 text-warm-700 rounded-lg hover:bg-warm-50 transition-colors text-sm font-medium"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
